/**
 * The Altar Forge (docs/design/ui-simplify.md §4.4): one KMMO screen with a left rail
 * (Enhance | Craft | Sets). Mockups: docs/art/mockups/enhance.jpg and forge.jpg.
 *
 * Craft is "pick a slot, press CRAFT": the grade is automatic (bestCraftGrade, tap the Scale node
 * to cycle), a fitting Vault token is used for free, the Sigil is on for tier slots, the result
 * is equipped when better. The Odds panel shows Rare / Epic / Legendary, one pity bar and the
 * Venom toggle. Everything else (luck chips, floors, Fortune, the roll) is in the (i) sheet.
 *
 * Enhance is group B's `renderEnhance` (./enhance.ts), loaded lazily so the rail works whether
 * or not that view has landed; Sets is ./sets.ts `renderSets`.
 */
import {
  type CraftOdds,
  type Item,
  type ItemSlot,
  type OpError,
  type Profile,
  EYE_FRAGMENTS_PER_EYE,
  ITEM_SLOTS,
  VENOM_MAX_CRAFT,
  craftOdds,
  equipItem,
  equippedGear,
  isTierSlot,
  itemById,
  itemOwner,
  rarityTable,
  salvageYield,
  setPieces,
} from '@keystone/progression';
import '../kmmo/kmmo.css';
import {
  RESOURCE_ICON,
  attachHoverCard,
  gearCard,
  gearSlot,
  hideHoverCard,
  iconUrl,
  itemSlot,
  kmModal,
  kmToast,
  panel,
  primaryButton,
  progressBar,
  secondaryButton,
  sideNav,
  slotIcon,
  toggle,
  divider,
} from '../kmmo';
import { altarRing, rangeSlider, rarityBar, slotMark } from '../kmmo/extra-forge';
import type { MetaScreenContext, MetaScreenFactory } from '../context';
import type { RouteParams } from '../router';
import { el } from '../ui/dom';
import { SLOT_LABEL, itemName } from '../ui/itemText';
import { RARITY_LABEL } from '../ui/rarity';
import { RESOURCE_META, bundleText } from '../forge/resourceText';
import { localDateString, prefersReducedMotion } from '../forge/localDate';
import {
  type CraftChoice,
  type CraftOutcome,
  type ForgeRecipe,
  type ForgeTab,
  autoRecipe,
  craftCta,
  cycleGrade,
  defaultCraftSlot,
  epicPityBar,
  eyeEligible,
  forgeAttention,
  forgeTabFor,
  formatPct,
  luckChips,
  materialNodes,
  oddsBars,
  pityView,
  recipeSpec,
  slotOptions,
} from './forgeModel';
import { revealPlan } from './revealModel';
import { type RevealHandle, playNodeReveal } from './forgeReveal';
import { renderSets, setsAttention } from './sets';
import { renderEnhance } from './enhance';
import { metaShell } from '../kmmo/extra-lobby';
import { opErrorText } from './upgradeModel';
import './altarForge.css';

/** A rail view mounted in the Forge body (right of the rail). */
export interface ForgeView {
  dispose(): void;
  /** Esc / back: true when handled inside the view. */
  onBack?(): boolean;
}

const TITLE: Readonly<Record<ForgeTab, string>> = { enhance: 'Enhance', craft: 'Altar Forge', sets: 'Tier Sets' };

/** Remembered across visits in this page session. */
let venomOn = false;
let venomVials = 3;
const recent: string[] = [];
const seenRarities = new Set<string>();

export const forgeScreen: MetaScreenFactory = (ctx) => {
  const store = ctx.store;
  let tab: ForgeTab = forgeTabFor(String(ctx.routeId), ctx.params);
  const shell = metaShell(ctx, { title: TITLE[tab], info: () => infoContent(ctx, tab), className: 'kaf-screen' });
  const frame = shell.frame;
  const railHost = el('div', 'kaf-rail', frame.body);
  const view = el('div', 'kaf-view', frame.body);
  const rail = sideNav(RAIL, { active: tab, onChange: (id) => show(id as ForgeTab, {}) }, railHost);
  let current: ForgeView | null = null;

  function show(t: ForgeTab, params: RouteParams): void {
    current?.dispose();
    hideHoverCard();
    view.replaceChildren();
    view.dataset['tab'] = t;
    tab = t;
    frame.title.textContent = TITLE[t];
    rail.set(t);
    if (t === 'craft') current = renderCraft(ctx, view, params, { toEnhance: (itemId) => show('enhance', { itemId }) });
    else if (t === 'sets') current = renderSets(ctx, view, { onForge: (slot) => show('craft', { slot, sigil: true }) });
    else current = renderEnhance(ctx, view, params);
  }

  show(tab, ctx.params);
  const unsub = store.subscribe((p) => railDots(rail.el, p));
  railDots(rail.el, store.profile);

  return {
    el: shell.el,
    dispose() {
      unsub();
      current?.dispose();
      current = null;
      hideHoverCard();
      shell.dispose();
    },
    onBack() {
      return current?.onBack?.() ?? false;
    },
  };
};

const RAIL = [
  { id: 'enhance', label: 'Enhance', icon: 'diamond' },
  { id: 'craft', label: 'Craft', icon: 'hammer' },
  { id: 'sets', label: 'Sets', icon: 'layers' },
] as const;

/** Red dots on the rail: an affordable upgrade craft, a temper, a Sigil with a missing piece. */
function railDots(rail: HTMLElement, p: Profile): void {
  const att = forgeAttention(p, localDateString());
  const want: Record<ForgeTab, boolean> = { enhance: att.temper > 0, craft: att.craft || att.tokens > 0, sets: setsAttention(p) };
  const tabs = rail.querySelectorAll<HTMLElement>('.km-tab');
  (['enhance', 'craft', 'sets'] as const).forEach((id, i) => {
    const b = tabs[i];
    if (!b) return;
    const dot = b.querySelector('.km-dot');
    if (want[id] && !dot) el('span', 'km-dot', b);
    else if (!want[id] && dot) dot.remove();
  });
}

// ---------------------------------------------------------------------------
// Craft view (mockup forge.jpg)
// ---------------------------------------------------------------------------

/** Node centres in the graph box (% of its width / height); the ring links them to the core. */
const NODE_POS = { left: { x: 17, y: 20 }, right: { x: 83, y: 20 }, bottom: { x: 50, y: 85 } } as const;

function renderCraft(ctx: MetaScreenContext, host: HTMLElement, params: RouteParams, nav: { toEnhance(itemId: string): void }): ForgeView {
  const store = ctx.store;
  const date = localDateString();
  const reduced = prefersReducedMotion();
  const p0 = store.profile;
  const paramSlot = typeof params['slot'] === 'string' && (ITEM_SLOTS as readonly string[]).includes(params['slot']) ? (params['slot'] as ItemSlot) : undefined;
  let choice: CraftChoice = {
    slot: paramSlot ?? defaultCraftSlot(p0, date),
    venom: 0,
    ...(params['sigil'] === true ? { sigil: true } : {}),
  };
  let shownId: string | null = null;
  let reveal: RevealHandle | null = null;
  let crafted = false;

  const root = el('div', 'kaf-craft', host);
  const slots = panel({ className: 'kaf-slots' }, root);
  const graph = el('div', 'kaf-graph', root);
  const ringHost = el('div', 'kaf-ring', graph);
  const nodes = el('div', 'kaf-nodes', graph);
  const core = el('div', 'kaf-core', graph);
  const coreName = el('div', 'kaf-core-name', graph);
  const odds = panel({ title: 'Odds', className: 'kaf-odds' }, root);
  const actions = el('div', 'kaf-actions', root);
  const last = panel({ className: 'kaf-last' }, root);

  const venomNow = (p: Profile): number => (venomOn ? Math.max(0, Math.min(venomVials, p.wallet.venom, VENOM_MAX_CRAFT)) : 0);
  const recipeNow = (p: Profile): ForgeRecipe => autoRecipe(p, { ...choice, venom: venomNow(p) });

  function select(slot: ItemSlot): void {
    if (reveal && !reveal.done) reveal.skip();
    choice = { slot, venom: 0 };
    shownId = null;
    renderAll();
  }

  // ---- slots panel
  function renderSlots(p: Profile, r: ForgeRecipe): void {
    hideHoverCard();
    slots.body.replaceChildren();
    const grid = el('div', 'kaf-slot-grid', slots.body);
    const gear = equippedGear(p, p.mainClassId);
    for (const o of slotOptionsFor(p, r)) {
      const item = gear[o.slot] ?? null;
      const h = gearSlot(item, { slot: o.slot, caption: SLOT_LABEL[o.slot], selected: o.slot === r.slot, onClick: () => select(o.slot), size: 'md' }, grid);
      if (o.favored) slotMark(h.slot, 'star', 'Favored today: +2 Luck');
      if (o.worth) slotMark(h.slot, 'up', 'A craft here is an upgrade');
      if (item) attachHoverCard(h.slot, () => gearCard(item, { equippedSetCount: setPieces(gear, p.mainClassId) }));
    }
    const fav = el('div', 'kaf-fav', slots.body);
    el('span', 'kaf-fav-star', fav, '★');
    el('span', '', fav, 'Favored today');
  }

  // ---- node graph
  function renderNodes(p: Profile, r: ForgeRecipe, o: CraftOdds): void {
    nodes.replaceChildren();
    ringHost.replaceChildren();
    const mats = materialNodes(p, r, o);
    const links: { x: number; y: number }[] = [];
    const place = (wrap: HTMLElement, pos: { x: number; y: number }): void => {
      wrap.classList.add('kaf-node');
      wrap.style.left = `${pos.x}%`;
      wrap.style.top = `${pos.y}%`;
      links.push(pos);
    };
    mats.forEach((m, i) => {
      const scale = m.key.startsWith('scale.');
      const icon = m.key === 'vaultToken' ? RESOURCE_ICON['vaultToken']! : RESOURCE_ICON[m.key]!;
      const h = itemSlot({
        rarity: m.key === 'fangsteel' ? 'rare' : m.key === 'vaultToken' ? 'legendary' : GRADE_RARITY[r.grade],
        icon: iconUrl(icon),
        caption: m.label,
        caption2: m.key === 'vaultToken' ? 'Free craft' : `${m.have}/${m.need}`,
        captionTone: m.short ? 'bad' : m.key === 'vaultToken' ? 'gold' : 'good',
        size: 'md',
        onClick: scale ? () => setGrade(cycleGrade(store.profile, r.slot, r.grade)) : undefined,
        title: scale ? `${RESOURCE_META[m.key as 'scale.jade'].label} · item level ${o.ilvl}` : undefined,
      }, nodes);
      place(h.el, i === 0 ? NODE_POS.left : NODE_POS.right);
    });
    // Bottom node: the Sigil (tier slots, while one is held) or the Serpent's Eye (when eligible).
    const eyes = Math.floor(p.wallet.eyeFragments / EYE_FRAGMENTS_PER_EYE);
    if (isTierSlot(r.slot) && p.wallet.sigils > 0) {
      const h = itemSlot({
        rarity: 'legendary',
        icon: iconUrl(RESOURCE_ICON['sigils']!),
        dim: !r.sigil,
        caption: 'Altar Sigil',
        caption2: r.sigil ? 'Tier piece' : 'Off',
        captionTone: r.sigil ? 'gold' : undefined,
        size: 'md',
        onClick: () => setChoice({ sigil: !r.sigil, eye: false }),
        title: r.sigil ? 'Tap to craft without the Sigil' : 'Tap to add the Sigil: a tier set piece',
      }, nodes);
      place(h.el, NODE_POS.bottom);
    } else if (eyeEligible(p, r.classId, r.slot) && eyes > 0) {
      const h = itemSlot({
        rarity: 'legendary',
        icon: iconUrl('forge_eye'),
        dim: !r.eye,
        caption: 'Serpent’s Eye',
        caption2: r.eye ? 'Legendary roll' : 'Off',
        captionTone: r.eye ? 'gold' : undefined,
        size: 'md',
        onClick: () => setChoice({ eye: !r.eye, sigil: false }),
      }, nodes);
      place(h.el, NODE_POS.bottom);
    }
    altarRing(links, ringHost);
  }

  function renderCore(p: Profile, r: ForgeRecipe): void {
    if (reveal && !reveal.done) return;
    core.replaceChildren();
    core.classList.remove('settled', 'hit', 'revealing', 'pulse');
    core.style.removeProperty('--c');
    coreName.replaceChildren();
    const item = shownId ? itemById(p, shownId) : undefined;
    if (item) {
      fillResult(core, item);
      return;
    }
    const box = el('div', 'kaf-core-box', core);
    const g = slotIcon(r.slot);
    g.classList.add('kaf-core-ghost');
    box.appendChild(g);
    el('span', 'kaf-core-q', box, '?');
  }

  function fillResult(h: HTMLElement, item: Item): void {
    h.replaceChildren();
    const p = store.profile;
    const live = itemById(p, item.id) ?? item;
    const s = gearSlot(live, { size: 'lg', equipped: !!itemOwner(p, live.id), onClick: () => itemSheet(live.id) }, h);
    s.el.classList.add('kaf-core-item');
    attachHoverCard(s.slot, () => gearCard(live));
    coreName.replaceChildren();
    const n = el('div', 'kaf-core-title', coreName, itemName(live));
    n.dataset['rarity'] = live.rarity;
    const owner = itemOwner(p, live.id);
    el('div', `kaf-core-sub${owner ? ' good' : ''}`, coreName, owner ? `${RARITY_LABEL[live.rarity]} · Equipped` : `${RARITY_LABEL[live.rarity]} · In bag`);
  }

  // ---- odds panel
  function renderOdds(p: Profile, o: CraftOdds): void {
    odds.body.replaceChildren();
    const rows = el('div', 'kaf-odds-rows', odds.body);
    for (const b of oddsBars(o)) rarityBar(b.rarity, RARITY_LABEL[b.rarity], b.pct, b.label, rows);
    divider(odds.body);
    const pity = epicPityBar(o.pity);
    el('div', 'kaf-pity-label', odds.body, pity.label);
    progressBar({ value: pity.filled, max: pity.segments, segments: Math.min(pity.segments, 12), tone: 'gold' }, odds.body).el.classList.add('kaf-pity');
    divider(odds.body);
    const have = Math.min(p.wallet.venom, VENOM_MAX_CRAFT);
    toggle({
      on: venomOn && have > 0,
      label: 'Add Venom (+luck)',
      icon: iconUrl(RESOURCE_ICON['venom']!, true),
      disabled: have < 1,
      onChange: (on) => {
        venomOn = on;
        if (on && venomVials < 1) venomVials = Math.min(3, have);
        renderAll();
      },
    }, odds.body).el.classList.add('kaf-venom');
    rangeSlider({
      value: venomOn ? Math.min(venomVials, have) : 0,
      min: 0,
      max: have,
      disabled: have < 1,
      label: 'Venom vials',
      onChange: (v) => {
        venomVials = v;
        venomOn = v > 0;
        renderAll();
      },
    }, odds.body).el.classList.add('kaf-venom-range');
  }

  // ---- actions
  function renderActions(p: Profile, o: CraftOdds): void {
    actions.replaceChildren();
    const cta = craftCta(p, o);
    const row = el('div', 'kaf-actions-row', actions);
    primaryButton('Craft', {
      size: 'lg',
      cost: cta.gold > 0 ? { amount: cta.gold } : undefined,
      sub: cta.free ? 'Free' : undefined,
      disabled: cta.disabled,
      title: cta.reason ?? (o.cost ? bundleText(o.cost, 'Free') : ''),
      onClick: () => doCraft(),
      className: 'kaf-craft-btn',
    }, row);
    const plain = craftOdds(p, recipeSpec(autoRecipe(p, { ...choice, venom: venomNow(p), sigil: false, eye: false }), date));
    secondaryButton('Craft x5', {
      size: 'lg',
      disabled: !!plain.blocked,
      title: 'Up to 5 plain crafts; stops on Epic or better',
      onClick: () => doBatch(),
      className: 'kaf-x5-btn kaf-sec-lg',
    }, row);
    if (cta.reason) el('div', 'kaf-reason', actions, cta.reason);
  }

  // ---- last crafts
  function renderLast(p: Profile): void {
    last.body.replaceChildren();
    const row = el('div', 'kaf-last-row', last.body);
    el('div', 'kaf-last-title', row, 'Last crafts');
    const items = recent.map((id) => itemById(p, id)).filter((x): x is Item => !!x).slice(0, 5);
    const strip = el('div', 'kaf-last-items', row);
    for (const it of items) {
      const h = gearSlot(it, { size: 'md', equipped: !!itemOwner(p, it.id), onClick: () => itemSheet(it.id) }, strip);
      attachHoverCard(h.slot, () => gearCard(it, { hint: 'Click for Equip / Salvage' }));
    }
    for (let i = items.length; i < 5; i++) itemSlot({ empty: true, size: 'md' }, strip);
  }

  function itemSheet(id: string): void {
    hideHoverCard();
    const p = store.profile;
    const item = itemById(p, id);
    if (!item) return;
    const m = kmModal(itemName(item));
    const box = el('div', 'kaf-sheet-card', m.body);
    gearCard(item, {}, box);
    const owner = itemOwner(p, id);
    if (owner) {
      secondaryButton('Enhance', { onClick: () => { m.close(); nav.toEnhance(id); } }, m.footer);
    } else {
      const eq = equipItem(p, p.mainClassId, id);
      secondaryButton('Equip', {
        disabled: !eq.ok,
        title: eq.ok ? '' : opErrorText(eq.reason),
        onClick: () => {
          m.close();
          void store.act('equip', { classId: store.profile.mainClassId, itemId: id }).then((r) => (r.ok ? kmToast('Equipped', 'success', 1400) : kmToast(opErrorText(r.reason as OpError), 'error')));
        },
      }, m.footer);
      const y = salvageYield(item);
      secondaryButton('Salvage', {
        sub: bundleText(y),
        onClick: () => {
          m.close();
          void store.act('salvage', { itemId: id }).then((r) => (r.ok ? kmToast(`Salvaged: +${bundleText(y)}`, 'success', 1800) : kmToast(opErrorText(r.reason as OpError), 'error')));
        },
      }, m.footer);
    }
  }

  // ---- crafting
  function setChoice(patch: Partial<CraftChoice>): void {
    choice = { ...choice, ...patch };
    shownId = null;
    renderAll();
  }
  function setGrade(g: CraftChoice['grade']): void {
    setChoice({ grade: g });
  }

  function remember(out: CraftOutcome): void {
    const i = recent.indexOf(out.item.id);
    if (i >= 0) recent.splice(i, 1);
    recent.unshift(out.item.id);
    recent.length = Math.min(recent.length, 5);
  }

  function startReveal(out: CraftOutcome, setCrossed: number | null): void {
    reveal?.dispose();
    const rarity = out.item.rarity;
    const plan = revealPlan({ rarity, tierPiece: out.roll.tierPiece, nearMiss: out.roll.nearMiss, seen: seenRarities.has(rarity), reducedMotion: reduced, setCrossed });
    seenRarities.add(rarity);
    shownId = out.item.id;
    coreName.replaceChildren();
    reveal = playNodeReveal(core, {
      plan,
      settle: (h) => fillResult(h, out.item),
      onDone: () => {
        if (setCrossed) kmToast(`${setCrossed}-piece set bonus active!`, 'success', 2200);
        else if (out.roll.nearMiss) kmToast('So close! Epic pity +2', 'info', 1600);
      },
    });
  }

  /** A roll is with the server (online): ignore presses until it answers. */
  let rolling = false;

  function doCraft(): void {
    if (reveal && !reveal.done) {
      reveal.skip();
      return;
    }
    if (rolling) return;
    const p = store.profile;
    const r = recipeNow(p);
    const o = craftOdds(p, recipeSpec(r, date));
    if (o.blocked) {
      kmToast(craftCta(p, o).reason ?? 'Can’t craft that right now.', 'error');
      return;
    }
    const before = setPieces(equippedGear(p, r.classId), r.classId);
    rolling = true;
    // The roll runs through the intent table: in the browser offline, on the server online.
    void store.act('craft', { recipe: r }).then((res) => {
      rolling = false;
      if (!res.ok) {
        kmToast(opErrorText(res.reason as OpError), 'error');
        return;
      }
      const after = setPieces(equippedGear(res.profile, r.classId), r.classId);
      const crossed = [4, 2].find((t) => before < t && after >= t) ?? null;
      crafted = true;
      remember(res.out);
      // A one-shot Sigil / Eye choice returns to automatic after it is spent.
      if (choice.sigil || choice.eye) choice = { ...choice, sigil: undefined, eye: false };
      startReveal(res.out, crossed);
    });
  }

  function doBatch(): void {
    if (reveal && !reveal.done) reveal.skip();
    if (rolling) return;
    const p = store.profile;
    rolling = true;
    void store.act('craftBatch', { choice: { ...choice, venom: venomNow(p) }, n: 5 }).then((b) => {
      rolling = false;
      if (!b.ok || !b.results.length) {
        kmToast('Not enough to craft.', 'error');
        return;
      }
      crafted = true;
      for (const out of b.results) remember(out);
      const best = b.results[b.results.length - 1]!;
      startReveal(best, null);
      kmToast(b.stopped === 'epic' ? `${RARITY_LABEL[best.item.rarity]} after ${b.results.length} craft${b.results.length === 1 ? '' : 's'}!` : `${b.results.length} crafts`, b.stopped === 'epic' ? 'success' : 'info', 1800);
    });
  }

  function renderAll(): void {
    const p = store.profile;
    const r = recipeNow(p);
    const o = craftOdds(p, recipeSpec(r, date));
    renderSlots(p, r);
    renderNodes(p, r, o);
    renderCore(p, r);
    renderOdds(p, o);
    renderActions(p, o);
    renderLast(p);
  }

  renderAll();
  const unsub = store.subscribe(() => renderAll());
  const onKey = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || e.repeat || e.code !== 'Space' || !crafted) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.tagName === 'BUTTON')) return;
    if (reveal && !reveal.done) return;
    e.preventDefault();
    doCraft();
  };
  window.addEventListener('keydown', onKey);

  return {
    dispose() {
      unsub();
      reveal?.dispose();
      window.removeEventListener('keydown', onKey);
      hideHoverCard();
    },
    onBack() {
      if (reveal && !reveal.done) {
        reveal.skip();
        return true;
      }
      return false;
    },
  };
}

const GRADE_RARITY = { ashen: 'common', jade: 'uncommon', gilded: 'legendary', eternal: 'epic' } as const;

function slotOptionsFor(p: Profile, r: ForgeRecipe): { slot: ItemSlot; favored: boolean; worth: boolean }[] {
  return slotOptions(p, p.mainClassId, r.grade, localDateString()).map((o) => ({ slot: o.slot, favored: o.favored, worth: o.worth && (o.gain > 0 || !o.current) }));
}

// ---------------------------------------------------------------------------
// (i) sheets
// ---------------------------------------------------------------------------

function infoContent(ctx: MetaScreenContext, tab: ForgeTab): HTMLElement {
  const p = ctx.store.profile;
  const box = el('div', 'kaf-info');
  const line = (head: string, text: string): void => {
    const r = el('div', 'kaf-info-row', box);
    el('b', '', r, head);
    el('span', '', r, text);
  };
  if (tab === 'craft') {
    const o = craftOdds(p, recipeSpec(autoRecipe(p, { slot: defaultCraftSlot(p), venom: 0 }), localDateString()));
    const pv = pityView(o.pity);
    const luck = luckChips(o.luck);
    line('Craft', 'Pick a slot and press Craft. The best Scale grade you can pay is used; tap the Scale to pick another. Better results are equipped for you.');
    line('Odds', `One roll lands in a rarity band; Luck moves the bands up. ${luck.label}${luck.chips.length ? ` (${luck.chips.map((c) => `${c.label} +${c.value}`).join(', ')})` : ''}.`);
    line('Pity', `${pv.epic.label}. A near miss counts double. ${pv.rareFloor.label}. ${pv.fortune.label}.`);
    line('Sigil', 'On a helm, chest, legs, gloves or boots, an Altar Sigil makes a tier set piece (Rare or better).');
    line('Vault token', 'A Vault Forge Token is spent for a free craft when it matches the grade.');
    line('Venom', `+1 Luck per vial, up to ${VENOM_MAX_CRAFT}.`);
    const t = el('div', 'kaf-info-table', box);
    for (const r of rarityTable().filter((x) => x.luck % 4 === 0)) el('span', '', t, `Luck ${r.luck}: Rare+ ${formatPct(r.rarePlus)} · Epic ${formatPct(r.odds.epic)}`);
  } else if (tab === 'sets') {
    line('Pieces', 'Your hero’s set has 5 pieces: helm, chest, legs, gloves and boots. 2 and 4 pieces unlock bonuses.');
    line('Sigils', 'Altar Sigils come from the Daily Offering, the first timed clear of each key bracket, the Vault and the Weekly Ritual.');
    line('Forge', 'Forge crafts the missing piece with a Sigil. Convert turns an item you own into a set piece and keeps its stats.');
  } else {
    line('Enhance', 'Each success adds +1: 5 item levels. A failure never breaks the item: it refunds half, raises the next chance and fills Resonance. Full Resonance guarantees the next try.');
    line('Awaken', 'Every 5th step Awakens the item: an empowered stat, a socket, a refund or a rarity upgrade.');
    line('Cap', 'At your rank’s item level cap, Enhance becomes Masterwork.');
    line('Blessing', 'A Blessed Charge adds +15% while the chance is below 100%. Tap the chip to keep your charges.');
  }
  return box;
}

/** Open the Forge on a tab (used by other screens). */
export function openForge(ctx: MetaScreenContext, params: { tab?: ForgeTab; slot?: string; sigil?: boolean; itemId?: string } = {}): void {
  ctx.router.open('forge', params);
}
